import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { Loader2, Pencil } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import type { ClinicFieldKey } from "@/api/db/connection";
import { ClinicProfileFields } from "@/api/document/clinic-profile";
import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { OverlayShell } from "@/components/ui/overlay";
import { SkeletonRows } from "@/components/ui/skeleton";
import { DocumentFieldLabels } from "@/lib/labels/document-fields";

import { SectionCard } from "./section-card";

const formSchema = type({ value: type("string.trim").to("string > 0") });

export function DocumentProfileSection() {
	const { data } = useQuery(orpc.documents.profile.list.queryOptions());
	const [editing, setEditing] = useState<{ key: ClinicFieldKey; value: string } | null>(null);

	return (
		<SectionCard title={DocumentFieldLabels.profile.title} dataComponent="system-document-profile">
			<p className="mb-2 text-xs text-muted-foreground">
				{DocumentFieldLabels.profile.description}
			</p>

			{data === undefined && <SkeletonRows rows={5} />}

			{data !== undefined && (
				<ul className="flex flex-col divide-y divide-border">
					{data.map((row) => (
						<li key={row.key} className="flex items-center gap-3 py-2">
							<div className="min-w-0 flex-1">
								<p className="text-xs text-muted-foreground">
									{ClinicProfileFields[row.key].label}
								</p>
								<p className="truncate text-sm font-medium text-foreground">{row.value}</p>
							</div>

							<Button
								size="icon"
								variant="ghost"
								className="size-8"
								aria-label={DocumentFieldLabels.profile.edit(ClinicProfileFields[row.key].label)}
								onClick={() => setEditing({ key: row.key, value: row.value })}
							>
								<Pencil />
							</Button>
						</li>
					))}
				</ul>
			)}

			{editing !== null && (
				<DocumentProfileEditModal value={editing} onClose={() => setEditing(null)} />
			)}
		</SectionCard>
	);
}

function DocumentProfileEditModal({
	value,
	onClose,
}: {
	value: { key: ClinicFieldKey; value: string };
	onClose(): void;
}) {
	const queryClient = useQueryClient();
	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid },
	} = useForm<typeof formSchema.infer>({
		resolver: arktypeResolver(formSchema),
		mode: "onChange",
		defaultValues: { value: value.value },
	});
	const { mutate, isPending } = useMutation(
		orpc.documents.profile.update.mutationOptions({
			onSuccess: () => {
				void queryClient.invalidateQueries({ queryKey: orpc.documents.profile.list.key() });
				toast.success(DocumentFieldLabels.profile.saved);
				onClose();
			},
			onError: () => toast.error(DocumentFieldLabels.profile.error),
		}),
	);
	const submit = handleSubmit((form) => mutate({ key: value.key, value: form.value }));
	const title = ClinicProfileFields[value.key].label;

	return (
		<OverlayShell label={title} onClose={onClose} className="items-center justify-center p-4">
			<form onSubmit={submit} className="overlay-panel max-w-md gap-4 p-5">
				<h2 className="type-section-title text-foreground">{title}</h2>

				<Controller
					control={control}
					name="value"
					render={({ field }) => (
						<FormField
							label={DocumentFieldLabels.profile.value}
							value={field.value}
							onChange={field.onChange}
							autoFocus
						/>
					)}
				/>

				<div className="flex justify-end gap-2">
					<Button type="button" variant="secondary" size="sm" onClick={onClose}>
						{DocumentFieldLabels.cancel}
					</Button>

					<Button
						variant="secondary"
						size="sm"
						type="submit"
						disabled={!isDirty || !isValid || isPending}
					>
						{isPending && <Loader2 className="size-4 animate-spin" />}
						{DocumentFieldLabels.save}
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}
